<script setup lang="ts">
/**
 * ImageAttachment — input por foto (A3 da Proposta Lume, Fase 1).
 *
 * Aceita imagem via:
 * - File picker (input type=file)
 * - Drag and drop
 * - Paste de clipboard (Cmd+V / Ctrl+V sobre a área)
 *
 * Converte para data URL e emite via `attach`. O componente NÃO envia
 * nada para lugar nenhum — emite para o pai decidir. Constitucional:
 *
 * - CL-08: foto fica em memória até o pai decidir. Nada vai pra rede sem
 *   consentimento adicional (que o app já exige via prompt do SO).
 * - LGPD: se a foto contiver pessoas, o controle é do aprendiz.
 * - P-UX-02 (One thing at a time): foto é aditiva, não substitui texto.
 */
import { ref } from "vue";

interface Props {
  /** Limite de tamanho em MB. Default 5. */
  maxSizeMB?: number;
  /** Tipos MIME aceitos. */
  accept?: string;
}

const props = withDefaults(defineProps<Props>(), {
  maxSizeMB: 5,
  accept: "image/*",
});

interface Emits {
  (e: "attach", dataUrl: string, filename: string): void;
  (e: "error", message: string): void;
}

const emit = defineEmits<Emits>();

const fileInputRef = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const lastError = ref<string | null>(null);
const previewDataUrl = ref<string | null>(null);
const lastFilename = ref<string | null>(null);

function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Falha ao ler arquivo"));
    reader.readAsDataURL(file);
  });
}

async function handleFile(file: File) {
  lastError.value = null;
  if (!isImageFile(file)) {
    const msg = "Apenas imagens são aceitas.";
    lastError.value = msg;
    emit("error", msg);
    return;
  }
  if (file.size > props.maxSizeMB * 1024 * 1024) {
    const msg = `Arquivo maior que ${props.maxSizeMB}MB.`;
    lastError.value = msg;
    emit("error", msg);
    return;
  }
  try {
    const dataUrl = await readAsDataURL(file);
    previewDataUrl.value = dataUrl;
    lastFilename.value = file.name;
    emit("attach", dataUrl, file.name);
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Erro desconhecido";
    lastError.value = msg;
    emit("error", msg);
  }
}

function onFileChange(e: Event) {
  const target = e.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) void handleFile(file);
}

function onClick() {
  fileInputRef.value?.click();
}

function onDragEnter(e: DragEvent) {
  e.preventDefault();
  isDragging.value = true;
}
function onDragLeave(e: DragEvent) {
  e.preventDefault();
  isDragging.value = false;
}
function onDragOver(e: DragEvent) {
  e.preventDefault();
  isDragging.value = true;
}
function onDrop(e: DragEvent) {
  e.preventDefault();
  isDragging.value = false;
  const file = e.dataTransfer?.files?.[0];
  if (file) void handleFile(file);
}

function onPaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items;
  if (!items) return;
  for (const item of items) {
    if (item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) {
        e.preventDefault();
        void handleFile(file);
        return;
      }
    }
  }
}

function clearPreview() {
  previewDataUrl.value = null;
  lastFilename.value = null;
}
</script>

<template>
  <div
    class="hlos-image-attachment"
    :class="{ 'hlos-image-attachment--dragging': isDragging }"
    role="region"
    aria-label="Anexar imagem"
    @paste="onPaste"
    @dragenter="onDragEnter"
    @dragleave="onDragLeave"
    @dragover="onDragOver"
    @drop="onDrop"
  >
    <input
      ref="fileInputRef"
      type="file"
      :accept="accept"
      class="hlos-image-attachment__input"
      data-testid="image-attachment-input"
      @change="onFileChange"
    />

    <div v-if="!previewDataUrl" class="hlos-image-attachment__cta">
      <button
        type="button"
        class="hlos-image-attachment__btn"
        data-testid="image-attachment-button"
        @click="onClick"
      >
        <span aria-hidden="true">📎</span>
        <span>Anexar imagem</span>
      </button>
      <p class="hlos-image-attachment__hint">
        Arraste, cole (Cmd+V) ou escolha um arquivo. {{ maxSizeMB }}MB máx.
      </p>
    </div>

    <div v-else class="hlos-image-attachment__preview">
      <img
        :src="previewDataUrl"
        :alt="lastFilename ?? 'Imagem anexada'"
        class="hlos-image-attachment__img"
        data-testid="image-attachment-preview"
      />
      <div class="hlos-image-attachment__meta">
        <span>{{ lastFilename }}</span>
        <button
          type="button"
          class="hlos-image-attachment__remove"
          aria-label="Remover imagem"
          @click="clearPreview"
        >
          Remover
        </button>
      </div>
    </div>

    <p
      v-if="lastError"
      class="hlos-image-attachment__error"
      role="alert"
    >
      {{ lastError }}
    </p>
  </div>
</template>

<style scoped>
.hlos-image-attachment {
  border: 2px dashed var(--hlos-color-neutral-300);
  border-radius: 8px;
  padding: 0.75rem;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.hlos-image-attachment--dragging {
  border-color: var(--hlos-color-primary);
  background: var(--hlos-color-hover);
}
.hlos-image-attachment__input {
  display: none;
}
.hlos-image-attachment__cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}
.hlos-image-attachment__btn {
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.hlos-image-attachment__btn:hover {
  background: var(--hlos-color-hover);
}
.hlos-image-attachment__hint {
  margin: 0;
  font-size: 0.85em;
  color: var(--hlos-color-neutral-600);
}
.hlos-image-attachment__preview {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
.hlos-image-attachment__img {
  max-width: 120px;
  max-height: 120px;
  border-radius: 6px;
  border: 1px solid var(--hlos-color-neutral-200);
  object-fit: cover;
}
.hlos-image-attachment__meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
}
.hlos-image-attachment__meta span {
  font-size: 0.85em;
  color: var(--hlos-color-neutral-600);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hlos-image-attachment__remove {
  font: inherit;
  align-self: flex-start;
  padding: 0.25rem 0.75rem;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 0.85em;
}
.hlos-image-attachment__error {
  margin: 0.5rem 0 0;
  font-size: 0.85em;
  color: var(--hlos-color-error);
}
</style>
